import { FeatureConfig, RouteKey } from "./environment";

import { ROUTES } from "@/constants/routes_sidebar/routes-constant";

const uuidRegex =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const isRouteEnabled = (
  currentPath: string,
  config: FeatureConfig,
): boolean => {
  if (config === "*") {
    return true;
  }
  const exactRouteKey = Object.entries(ROUTES).find(
    ([_, path]) => path === currentPath,
  )?.[0] as RouteKey;

  if (exactRouteKey) {
    return config[exactRouteKey] === true;
  }

  const matchingRoutes = ((): string[][] => {
    // Get base matching routes
    const baseMatches = Object.entries(ROUTES).filter(([_, routePath]) => {
      return currentPath.startsWith(routePath) && currentPath !== routePath;
    });

    // Find the longest matching route to extract sub-path
    const longestMatch = baseMatches
      .map(([_, routePath]) => routePath)
      .sort((a, b) => b.length - a.length)[0];

    const results = [];

    // Check if remaining part after longest match is a UUID
    if (longestMatch && currentPath.startsWith(longestMatch)) {
      const remaining = currentPath.substring(longestMatch.length);
      if (remaining.startsWith("/")) {
        const pathAfterSlash = remaining.substring(1);
        const firstPart = pathAfterSlash.split("?")[0].split("/")[0];

        if (uuidRegex.test(firstPart)) {
          const longestRouteEntry = baseMatches.find(
            ([_, routePath]) => routePath === longestMatch,
          );

          return longestRouteEntry ? [longestRouteEntry] : [];
        }

        if (pathAfterSlash && !pathAfterSlash.includes("?")) {
          const subPathPart = pathAfterSlash.split("/")[0];
          results.push([subPathPart.toUpperCase(), "/" + subPathPart]);
        }
      }
    }

    const filteredMatches = baseMatches.filter(
      ([_, routePath]) => routePath !== longestMatch && routePath !== "/",
    );
    results.push(...filteredMatches);

    return results;
  })();

  if (matchingRoutes.length === 0) {
    return true;
  }

  matchingRoutes.sort(([, a], [, b]) => b.length - a.length);

  // Check if any matching parent route is enabled
  for (const [routeKey] of matchingRoutes) {
    if (config[routeKey.replace("-", "_") as RouteKey] === true) {
      return true;
    }
  }

  return false;
};

export const getRouteKeyFromPath = (path: string): RouteKey | null => {
  const exactMatch = Object.entries(ROUTES).find(
    ([_, routePath]) => routePath === path,
  );
  if (exactMatch) {
    return exactMatch[0] as RouteKey;
  }

  const parentRoutes = Object.entries(ROUTES)
    .filter(
      ([_, routePath]) => path.startsWith(routePath) && path !== routePath,
    )
    .sort(([, a], [, b]) => b.length - a.length); // Longest match first

  return parentRoutes.length > 0 ? (parentRoutes[0][0] as RouteKey) : null;
};
